"use client"

import React, { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import AdCard from "./AdCard"
import { StarIcon } from "@heroicons/react/24/solid"
import { SlidersHorizontal, MapPin, Navigation, Heart, Search } from "lucide-react"
import { ref, onValue, query, orderByChild, set, remove, get } from "../lib/firebase-database"
import { db, auth } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { MobileBottomNav } from "../components/MobileBottomNav"
import FilterMenu from "./filter-menu"
import { getCachedLocation, cacheLocation } from "../utils/locationUtils"
import { Loader2 } from "lucide-react"
import { getOptimizedUrl } from "./OptimizedImage"
// import BotpressChat from "./BotpressChat"

const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIntersecting] = useState(false)
  const [node, setNode] = useState(null)

  useEffect(() => {
    if (!node) return

    const observer = new IntersectionObserver(([entry]) => {
      setIntersecting(entry.isIntersecting)
    }, options)

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [node, options])

  return [setNode, isIntersecting]
}

const LazyImage = ({ src, alt, className }) => {
  const [ref, isIntersecting] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "50px",
  })
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(false)
  }, [src])
  const optimizedSrc = src ? getOptimizedUrl(src, 480, 300, 75) : "/placeholder.svg"

  return (
    <div ref={ref} className={`relative ${className}`}>
      {isIntersecting ? (
        <>
          <img
            src={optimizedSrc}
            alt={alt}
            className={`${className} ${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
            decoding="async"
          />
          {!isLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />}
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />
      )}
    </div>
  )
}

export default function ExplorePage({ cachedProperties, updateCachedProperties }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [properties, setProperties] = useState([])
  const [allProperties, setAllProperties] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState("random")
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [isBlurred, setIsBlurred] = useState(false)
  const { user, isAdmin, isOwner } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [wishlistedProperties, setWishlistedProperties] = useState({})
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768)
  const [userLocation, setUserLocation] = useState(null)
  const [userCity, setUserCity] = useState("")
  const [userState, setUserState] = useState("")
  const [isRefreshingLocation, setIsRefreshingLocation] = useState(false)
  const [filteredProperties, setFilteredProperties] = useState([])
  const [title, setTitle] = useState("Explore Villas")
  const [showBot, setShowBot] = useState(false)
  const [isFilteringLoading, setIsFilteringLoading] = useState(false)
  const [checkInDate, setCheckInDate] = useState(null)
  const [checkOutDate, setCheckOutDate] = useState(null)
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [selectedCity, setSelectedCity] = useState("")
  const [cityList, setCityList] = useState([])
  const [isSearchVisible, setIsSearchVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isSearchTransitionActive, setIsSearchTransitionActive] = useState(
    Boolean(location.state?.fromHomeSearchTransition),
  )

  useEffect(() => {
    // Use cached location immediately
    const cachedData = getCachedLocation()
    if (cachedData) {
      setUserLocation(cachedData.location)
      setUserCity(cachedData.city)
      setUserState(cachedData.state)
    }

    fetchProperties()

    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768
      setIsMobile(newIsMobile)
      if (!newIsMobile) {
        setShowMobileMenu(false)
        setShowFilters(true)
      } else {
        setShowFilters(false)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (user) {
      const wishlistRef = ref(db, `wishlists/${user.uid}`)
      onValue(wishlistRef, (snapshot) => setWishlistedProperties(snapshot.val() || {}))
    }
  }, [user])

  useEffect(() => {
    if (location.state?.filterType && allProperties.length > 0) {
      handleInitialFilter(location.state)
    }
  }, [location.state, allProperties])

  useEffect(() => {
    if (!location.state?.fromHomeSearchTransition) return

    const raf = window.requestAnimationFrame(() => {
      setIsSearchTransitionActive(false)
    })

    return () => window.cancelAnimationFrame(raf)
  }, [location.state?.fromHomeSearchTransition])

  useEffect(() => {
    const incomingQuery = (location.state?.searchQuery || "").trim()
    if (!incomingQuery || allProperties.length === 0) return

    setSearchQuery(incomingQuery)

    const searchTerms = incomingQuery.toLowerCase().split(" ").filter(Boolean)
    const filtered = allProperties.filter((property) => {
      const propertyText = `${property.name || ""} ${property.mainCity || ""} ${property.address || ""} ${(property.facilities || []).join(" ")}`.toLowerCase()
      return searchTerms.every((term) => propertyText.includes(term))
    })

    setProperties(filtered)
  }, [location.state?.searchQuery, allProperties])

  useEffect(() => {
    const locationState = location.state
    if (locationState?.filterType === "occasion") {
      const facilities = locationState.facilities || []
      const filtered = properties.filter((property) =>
        facilities.every((facility) => property.facilities?.includes(facility)),
      )
      setFilteredProperties(filtered)
      setProperties(filtered)
      setTitle(`Properties for ${locationState.occasionTitle}`)
      document.title = `${locationState.occasionTitle} - VillaKosh`
    }
  }, [location, properties])

  useEffect(() => {
    document.title = `${title} - VillaKosh`
  }, [title])

  const fetchProperties = async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (cachedProperties.length > 0) {
        const visibleProperties = cachedProperties.filter((property) => !property.isHidden)
        await new Promise((resolve) => setTimeout(resolve, 1000))
        setProperties(shuffleArray([...visibleProperties]))
        setAllProperties(visibleProperties)
        setHasLoaded(true)
        setIsLoading(false)
      } else {
        const villasRef = ref(db, "villas")
        const villasQuery = query(villasRef, orderByChild("createdAt"))

        onValue(
          villasQuery,
          (snapshot) => {
            const data = snapshot.val()
            const fetchedProperties = data
              ? Object.entries(data)
                  .map(([key, value]) => ({
                    _id: key,
                    ...value,
                  }))
                  .filter((property) => !property.isHidden)
              : []
            setProperties(shuffleArray([...fetchedProperties]))
            setAllProperties(fetchedProperties)
            updateCachedProperties(fetchedProperties)
            setHasLoaded(true)
            setIsLoading(false)
          },
          (error) => {
            console.error("Error fetching properties:", error)
            setError("Failed to load properties. Please try again later.")
            setHasLoaded(true)
            setIsLoading(false)
          },
        )
      }
    } catch (error) {
      console.error("Error:", error)
      setError("An unexpected error occurred")
      setHasLoaded(true)
      setIsLoading(false)
    }
  }

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  const filterProperties = () => {
    if (searchQuery.length > 0) {
      const searchTerms = searchQuery.toLowerCase().split(" ")
      const filtered = allProperties.filter((property) => {
        const propertyText =
          `${property.name || ""} ${property.mainCity || ""} ${property.address || ""} ${(property.facilities || []).join(" ")}`.toLowerCase()
        return searchTerms.every((term) => propertyText.includes(term))
      })
      setProperties(filtered)
    } else {
      setProperties(shuffleArray([...allProperties]))
    }
  }

  const handleSort = (value) => {
    setSortBy(value)
    const sorted = [...properties]
    switch (value) {
      case "random":
        setProperties(shuffleArray([...allProperties]))
        break
      case "recommended":
        sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
        setProperties(sorted)
        break
      case "price-low-high":
        sorted.sort((a, b) => (a.rate || 0) - (b.rate || 0))
        setProperties(sorted)
        break
      case "price-high-low":
        sorted.sort((a, b) => (b.rate || 0) - (a.rate || 0))
        setProperties(sorted)
        break
      default:
        break
    }
    setShowSortMenu(false)
  }

  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      setIsMenuClosing(true)
      setTimeout(() => {
        setShowMobileMenu(false)
        setIsMenuClosing(false)
        setIsBlurred(false)
        document.body.style.overflow = "auto"
      }, 300)
    } else {
      setShowMobileMenu(true)
      setIsBlurred(true)
      document.body.style.overflow = "hidden"
    }
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const handleLogoutConfirm = () => {
    auth.signOut()
    setShowLogoutConfirmation(false)
    navigate("/")
  }

  const handleLogoutCancel = () => setShowLogoutConfirmation(false)

  const goToProfile = () => {
    navigate("/profile")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const toggleLoginPopup = (show) => {
    setShowLoginPopup(show)
    setIsBlurred(show)
    document.body.style.overflow = show ? "hidden" : "auto"
  }

  const getUserCityName = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
      )
      const data = await response.json()
      if (data.results && data.results[0]) {
        const addressComponents = data.results[0].address_components
        let city = "",
          state = ""
        for (const component of addressComponents) {
          if (component.types.includes("locality")) city = component.long_name
          if (component.types.includes("administrative_area_level_1")) state = component.long_name
        }
        setUserCity(city || "Location not available")
        setUserState(state || "")
      }
    } catch (error) {
      console.error("Error fetching location:", error)
      setUserCity("Location not available")
      setUserState("")
    }
  }

  const handleWishlistClick = async (propertyId) => {
    if (!user) {
      toggleLoginPopup(true)
      return
    }

    const wasWishlisted = isPropertyWishlisted(propertyId)
    setWishlistedProperties((prev) => {
      const next = { ...prev }
      if (wasWishlisted) delete next[propertyId]
      else next[propertyId] = true
      return next
    })

    const wishlistRef = ref(db, `wishlists/${user.uid}/${propertyId}`)
    try {
      if (wasWishlisted) await remove(wishlistRef)
      else await set(wishlistRef, true)
    } catch (error) {
      console.error("Wishlist update failed:", error)
      setWishlistedProperties((prev) => {
        const next = { ...prev }
        if (wasWishlisted) next[propertyId] = true
        else delete next[propertyId]
        return next
      })
      alert("Failed to update favorites. Please try again.")
    }
  }

  const isPropertyWishlisted = (propertyId) => !!wishlistedProperties[propertyId]

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371
    const dLat = deg2rad(lat2 - lat1)
    const dLon = deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const deg2rad = (deg) => {
    return deg * (Math.PI / 180)
  }

  const applyFilters = async (filters) => {
    setIsFilteringLoading(true)

    try {
      await new Promise((resolve) => setTimeout(resolve, 500))

      let filteredProperties = [...allProperties]

      if (filters.priceRange) {
        filteredProperties = filteredProperties.filter(
          (property) => property.rate >= filters.priceRange[0] && property.rate <= filters.priceRange[1],
        )
      }

      if (filters.rating > 0) {
        filteredProperties = filteredProperties.filter((property) => (property.averageRating || 0) >= filters.rating)
      }

      if (filters.facilities && filters.facilities.length > 0) {
        filteredProperties = filteredProperties.filter((property) =>
          filters.facilities.every((facility) => property.facilities && property.facilities.includes(facility)),
        )
      }

      if (filters.distance && userLocation) {
        filteredProperties = filteredProperties.filter((property) => {
          if (!property.location) return false
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            property.location.lat,
            property.location.lng,
          )
          return distance <= filters.distance
        })
      }

      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "recommended":
            filteredProperties.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
            break
          case "price-low-high":
            filteredProperties.sort((a, b) => (a.rate || 0) - (b.rate || 0))
            break
          case "price-high-low":
            filteredProperties.sort((a, b) => (b.rate || 0) - (a.rate || 0))
            break
          default:
            break
        }
      }

      setProperties(filteredProperties)
      if (isMobile) {
        setShowFilters(false)
      }
    } finally {
      setIsFilteringLoading(false)
    }
  }

  const handleLocationRefresh = async () => {
    setIsRefreshingLocation(true)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords
          const newLocation = { lat: latitude, lng: longitude }

          try {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
            )
            const data = await response.json()

            if (data.results?.[0]) {
              const addressComponents = data.results[0].address_components
              let city = "",
                state = ""

              for (const component of addressComponents) {
                if (component.types.includes("locality")) city = component.long_name
                if (component.types.includes("administrative_area_level_1")) state = component.long_name
              }

              setUserLocation(newLocation)
              setUserCity(city || "Location not available")
              setUserState(state || "")

              cacheLocation(newLocation, city, state)
            }
          } catch (error) {
            console.error("Error fetching location details:", error)
          }
        },
        (error) => {
          console.error("Error getting location:", error)
        },
      )
    }
    setIsRefreshingLocation(false)
  }

  const handleInitialFilter = (filterState) => {
    let filteredProperties = [...allProperties]
    let filterDescription = ""

    switch (filterState.filterType) {
      case "destination":
        filteredProperties = filteredProperties.filter(
          (property) => property.mainCity?.toLowerCase() === filterState.mainCity?.toLowerCase(),
        )
        filterDescription = `Villas in ${filterState.mainCity}`
        break

      case "valueStays":
        filteredProperties = filteredProperties.filter((property) => property.rate <= filterState.maxPrice)
        filterDescription = `Villas under ₹${filterState.maxPrice}`
        break

      case "nearby":
        if (userLocation) {
          filteredProperties = filteredProperties
            .map((property) => ({
              ...property,
              distance: property.location
                ? calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng)
                : Number.POSITIVE_INFINITY,
            }))
            .filter((property) => property.distance !== Number.POSITIVE_INFINITY)
            .sort((a, b) => a.distance - b.distance)
        }
        filterDescription = "Villas Near You"
        break

      case "luxury":
        filteredProperties = filteredProperties.filter((property) => property.rate >= 5000)
        filterDescription = "Luxury Villas"
        break

      default:
        filterDescription = "All Villas"
        break
    }

    setProperties(filteredProperties)
    setTitle(filterDescription)
    document.title = `${filterDescription} | VillaKosh`
  }

  const PropertySkeleton = () => (
  <div className="flex-shrink-0 w-full bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200 animate-pulse">
    <div className="relative p-2">
      <div className="rounded-[10px] overflow-hidden">
        <div className="grid grid-cols-2 gap-1 h-40">
          <div className="relative h-40 bg-gray-200 rounded-l-[20px]" />
          <div className="grid grid-rows-2 gap-2 h-40">
            <div className="h-[5rem] bg-gray-200 rounded-tr-[15px]" />
            <div className="h-[5rem] bg-gray-200 rounded-br-[15px]" />
          </div>
        </div>
      </div>
    </div>
    <div className="px-3 pb-2">
      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded-full" />
          <div className="ml-1 h-4 bg-gray-200 rounded w-1/3" />
        </div>
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded-full" />
          <div className="ml-1 h-4 bg-gray-200 rounded w-12" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="h-5 bg-gray-200 rounded w-1/4" />
        <div className="h-4 bg-gray-200 rounded w-16" />
      </div>
    </div>
  </div>
)

  const handleDateAvailabilityFilter = async () => {
    if (!checkInDate || !checkOutDate) return

    const startDate = new Date(checkInDate)
    const endDate = new Date(checkOutDate)

    setIsLoading(true)
    const filteredProps = selectedCity
      ? allProperties.filter((property) => property.mainCity?.toLowerCase() === selectedCity.toLowerCase())
      : allProperties

    const availableVillas = []

    for (const property of filteredProps) {
      const bookingsRef = ref(db, `bookings/${property._id}`)
      const snapshot = await get(bookingsRef)
      let isAvailable = true

      if (snapshot.exists()) {
        const bookings = Object.values(snapshot.val())
        for (const booking of bookings) {
          const bookedStart = new Date(booking.startDate)
          const bookedEnd = new Date(booking.endDate)
          if (startDate <= bookedEnd && endDate >= bookedStart) {
            isAvailable = false
            break
          }
        }
      }

      if (isAvailable) {
        availableVillas.push(property)
      }
    }

    setProperties(availableVillas)
    setIsLoading(false)
  }

  useEffect(() => {
    if (allProperties.length > 0) {
      const uniqueCities = Array.from(new Set(allProperties.map((p) => p.mainCity).filter(Boolean)))
      setCityList(uniqueCities)
    }
  }, [allProperties])

  const handleCitySelect = (city) => {
    setSelectedCity(city)
    setShowCityDropdown(false)
    setSearchQuery(city)
    const filtered = allProperties.filter((property) => property.mainCity?.toLowerCase() === city.toLowerCase())
    setProperties(filtered)
  }

  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && event.target.type !== "text") {
        setShowCityDropdown(false)
      }
    }
    if (showCityDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showCityDropdown])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setIsSearchVisible(currentScrollY < lastScrollY || currentScrollY < 100)
      setLastScrollY(currentScrollY)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [lastScrollY])

  // Helper to format date as "DD Mon"
  function formatDateDisplay(dateStr) {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    const day = date.getDate()
    const month = date.toLocaleString("en-US", { month: "short" })
    return `${day} ${month}`
  }

  // Get today's and tomorrow's date in yyyy-mm-dd format
  function getTodayDate() {
    const today = new Date()
    return today.toISOString().split("T")[0]
  }

  function getTomorrowDate() {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split("T")[0]
  }

  // Set default dates on mount
  useEffect(() => {
    setCheckInDate(getTodayDate())
    setCheckOutDate(getTomorrowDate())
  }, [])

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Sticky Search Bar */}
      <div
        className={`sticky top-0 z-50 bg-[#EDEFEF] transition-transform duration-300 ${
          isSearchVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="container mx-auto px-4 lg:px-16 py-4">
          <div
            className={`bg-white rounded-[24px] shadow-md p-4 transition-all duration-500 ${
              isSearchTransitionActive ? "opacity-0 -translate-y-4 scale-95" : "opacity-100 translate-y-0 scale-100"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-[2fr,2fr,auto] gap-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by city, villa name, facilities..."
                  value={searchQuery}
                  onFocus={() => setShowCityDropdown(true)}
                  onClick={() => setShowCityDropdown(true)}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    filterProperties()
                    setShowCityDropdown(true)
                  }}
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm transition-all"
                />
                {/* City Dropdown */}
                {showCityDropdown && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-0 right-0 mt-2 bg-white border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto"
                  >
                    <div className="sticky top-0 bg-white border-b flex justify-between items-center p-3">
                      <h3 className="font-semibold text-gray-700">Select City</h3>
                      <button
                        className="text-gray-500 hover:text-red-500 p-1"
                        onClick={() => setShowCityDropdown(false)}
                      >
                        ✕
                      </button>
                    </div>
                    {cityList.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 p-3">
                        {cityList
                          .filter((city) => city.toLowerCase().includes(searchQuery.toLowerCase()))
                          .map((city) => (
                            <div
                              key={city}
                              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                              onClick={() => handleCitySelect(city)}
                            >
                              <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-700 truncate">{city}</span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-gray-500">No cities found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Check-in and Check-out Dates */}
              {/* Check-in and Check-out Dates on same row */}
              <div className="flex gap-2">
                <div className="relative w-1/2">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <input
                    type="date"
                    value={checkInDate || ""}
                    onChange={(e) => setCheckInDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-full bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-sm"
                    min={getTodayDate()}
                    placeholder="Check-in"
                    style={{
                      colorScheme: "light",
                    }}
                  />
                  {/* Show formatted date only when input is not focused and has value */}
                  {checkInDate && (
                    <div className="absolute inset-0 flex items-center pl-10 pr-4 pointer-events-none opacity-0 peer-placeholder-shown:opacity-100">
                      <span className="text-gray-700 text-sm">{formatDateDisplay(checkInDate)}</span>
                    </div>
                  )}
                </div>
                <div className="relative w-1/2">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <input
                    type="date"
                    value={checkOutDate || ""}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-full bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all text-sm"
                    min={checkInDate || getTodayDate()}
                    placeholder="Check-out"
                    style={{
                      colorScheme: "light",
                    }}
                  />
                  {/* Show formatted date only when input is not focused and has value */}
                  {checkOutDate && (
                    <div className="absolute inset-0 flex items-center pl-10 pr-4 pointer-events-none opacity-0 peer-placeholder-shown:opacity-100">
                      <span className="text-gray-700 text-sm">{formatDateDisplay(checkOutDate)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Search Button */}
              <button
                className="bg-red-400 hover:bg-red-500 text-white px-8 py-3 rounded-full font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDateAvailabilityFilter}
                disabled={!checkInDate || !checkOutDate}
              >
                <Search className="h-5 w-5" />
                <span className="hidden md:inline">Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ad Card Section */}
      <div className="container mx-auto px-4 lg:px-16 mb-6">
        <AdCard
          adSlot="2298895661"
          className="w-full h-[100px] sm:h-[110px] md:h-[248px] lg:h-[248px] 
                    mx-auto rounded-xl overflow-hidden transition-all duration-300"
        />
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 lg:px-16 pb-20">
        <div className="lg:grid lg:grid-cols-[320px,1fr] lg:gap-6">
          {!isMobile && (
            <FilterMenu
              isOpen={true}
              onClose={() => {}}
              onApply={(filters) => {
                applyFilters(filters)
                if (filters.sortBy !== sortBy) {
                  setSortBy(filters.sortBy)
                }
              }}
              sortBy={sortBy}
              variant="sidebar"
            />
          )}
          <div>
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(8)].map((_, index) => (
                  <PropertySkeleton key={index} />
                ))}
              </div>
            ) : hasLoaded && properties.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <h2 className="text-xl font-semibold text-gray-600 mb-2">No villas found</h2>
                <p className="text-gray-500 text-center mb-4">We couldn't find any villas matching your criteria.</p>
                <button
                  onClick={() => {
                    setProperties([...allProperties])
                    setSearchQuery("")
                    navigate("/explore", { replace: true })
                  }}
                  className="px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  View All Villas
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 pb-32">
                {properties.map((property, index) => (
                  <React.Fragment key={property._id}>
                    <Link
                      to={`/villa/${property._id}`}
                      className="flex-shrink-0 w-full bg-white rounded-[20px] overflow-hidden border-2 border-gray-300"
                    >
                      <div className="relative p-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            handleWishlistClick(property._id)
                          }}
                          className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                        >
                          {isPropertyWishlisted(property._id) ? (
                            <Heart className="h-5 w-5 text-red-500 fill-current" />
                          ) : (
                            <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                          )}
                        </button>
                        <div className="rounded-[20px] overflow-hidden">
                          <div className="grid grid-cols-2 gap-1 h-40">
                            <div className="relative h-40">
                              <LazyImage
                                src={property.thumbnail || property.images[0]}
                                alt={property.name}
                                className="w-full h-full object-cover rounded-l-[20px]"
                              />
                            </div>
                            <div className="grid grid-rows-2 gap-2 h-40">
                              <div className="h-[5rem]">
                                <LazyImage
                                  src={property.images[1] || "/placeholder.svg"}
                                  alt={property.name}
                                  className="w-full h-full object-cover rounded-tr-[15px]"
                                />
                              </div>
                              <div className="h-[5rem]">
                                <LazyImage
                                  src={property.images[2] || "/placeholder.svg"}
                                  alt={property.name}
                                  className="w-full h-full object-cover rounded-br-[15px]"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="px-3 pb-2">
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                        <div className="mt-1 flex items-center justify-between text-sm text-gray-600">
                          <div className="flex items-center">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="ml-1 truncate">{property.mainCity}</span>
                          </div>
                          <div className="flex items-center">
                            <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                            <span className="ml-1 font-medium">
                              {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                            </span>
                            <span className="ml-1">({property.reviewCount || 0})</span>
                          </div>
                        </div>
                        <div className="mt-1 flex items-center justify-between">
                          <p className="text-base font-bold text-gray-900">
                            ₹{property.rate}
                            {property.pricingType === 'perPerson' && property.rate && (property.perPersonGuestCount || property.guestCount) && (
                              <span className="text-xs text-gray-600 ml-1">
                                Est.₹{Math.round(property.rate / (property.perPersonGuestCount || property.guestCount))}/guest
                              </span>
                            )}
                          </p>
                          <div className="text-sm text-gray-600 truncate">
                            {userLocation && property.location ? (
                              <span>{Math.round(calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng) * 10) / 10} km</span>
                            ) : (
                              <span>N/A</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* AI bot temporarily disabled
      <button
        onClick={() => setShowBot(true)}
        className="fixed z-[100] bottom-20 right-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-all w-16 h-16 sm:bottom-8 sm:w-20 sm:h-20"
        style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}
        aria-label="Open Bot"
      >
        <img src="/bot-icon.png" alt="Open Bot" className="w-full h-full object-cover rounded-full" />
      </button>

      {showBot && (
        <div className="fixed inset-0 z-[9999] flex items-end justify-end">
          <div className="w-full h-full">
            <BotpressChat />
          </div>
          <button
            onClick={() => setShowBot(false)}
            className="absolute top-4 right-4 z-[10000] bg-white text-black rounded-full p-2 shadow"
            aria-label="Close Bot"
          >
            ✕
          </button>
        </div>
      )}
      */}

      {/* Floating Filter Button */}
      {isMobile && (
        <button
          onClick={() => setShowFilters(true)}
          className="fixed bottom-20 left-4 z-50 bg-black text-white py-3 px-6 rounded-full shadow-lg flex items-center gap-2 hover:bg-gray-800 transition-all duration-300"
          disabled={isFilteringLoading}
        >
          {isFilteringLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <SlidersHorizontal className="h-5 w-5" />
          )}
          <span>{isFilteringLoading ? "Filtering..." : "Filter"}</span>
        </button>
      )}

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav user={user} className="h-16" />}

      {/* Filter Menu */}
      {isMobile && (
        <FilterMenu
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApply={(filters) => {
            applyFilters(filters)
            if (filters.sortBy !== sortBy) {
              setSortBy(filters.sortBy)
            }
          }}
          sortBy={sortBy}
          initialFilters={{
            priceRange: [0, 50000],
            rating: 0,
            facilities: [],
            distance: null,
          }}
        />
      )}
    </div>
  )
}
